import type { GovernanceVoting, ValidatorSnapshot, VoteResults, VoteStatus } from '@/types/governance';

export function calculateVoteResults(
    votesInput: GovernanceVoting['votes'],
    stakeData: ValidatorSnapshot
): VoteResults {
    // Calculate effective stake per validator
    const effectiveStake = new Map<string, bigint>();

    for (const [validator_address, stake_amount] of Object.entries(stakeData.all_stake)) {
        const ignored = stakeData.ignored_stake[validator_address] || 0n;
        effectiveStake.set(
            validator_address,
            stake_amount - ignored
        );
    }

    // Calculate weighted vote totals
    let yesStake = 0n;
    let noStake = 0n;
    let abstainStake = 0n;
    let totalStake = 0n;

    // Calculate total effective stake
    for (const stake of effectiveStake.values()) {
        totalStake += stake;
    }

    // Calculate vote-weighted stakes for validators who have voted
    const votedValidators = new Set<string>();
    for (const [validator_address, voteValue] of Object.entries(votesInput)) {
        const stake = effectiveStake.get(validator_address) || 0n;
        votedValidators.add(validator_address);

        switch (voteValue) {
            case 'Yes': yesStake += stake; break;
            case 'No': noStake += stake; break;
            case 'Abstain': abstainStake += stake; break;
        }
    }

    // Calculate stake for validators who haven't voted
    let notVotedStake = 0n;
    for (const [validator_address, stake] of effectiveStake.entries()) {
        if (!votedValidators.has(validator_address)) {
            notVotedStake += stake;
        }
    }

    // Calculate turnout (Yes + No votes only, abstain doesn't count toward turnout)
    const participatingStake = yesStake + noStake;
    const turnoutPercentage = totalStake > 0n ? (Number(participatingStake) / Number(totalStake)) * 100 : 0;

    // New passing logic: Yes > No AND turnout > 50%
    const isPassing = yesStake > noStake && turnoutPercentage > 50;

    return {
        yesPercentage: totalStake > 0n ? (Number(yesStake) / Number(totalStake)) * 100 : 0,
        noPercentage: totalStake > 0n ? (Number(noStake) / Number(totalStake)) * 100 : 0,
        abstainPercentage: totalStake > 0n ? (Number(abstainStake) / Number(totalStake)) * 100 : 0,
        notVotedPercentage: totalStake > 0n ? (Number(notVotedStake) / Number(totalStake)) * 100 : 0,
        isPassing,
        totalVotingStake: totalStake,
        yesStake,
        noStake,
        abstainStake,
        notVotedStake,
        turnoutPercentage,
    };
}

// Helper function to check if vote can pass early
export function checkEarlyPassingConditions(
    vote: GovernanceVoting,
    results: VoteResults
): { canPassEarly: boolean; reason?: string } {
    const now = Date.now();

    // Check 1: Minimum duration has elapsed
    if (now < vote.minimum_duration_timestamp_ms) {
        return { canPassEarly: false, reason: 'Minimum voting duration not yet reached' };
    }

    // Check 2: 50% participation (Yes + No votes)
    if (results.turnoutPercentage <= 50) {
        return { canPassEarly: false, reason: 'Insufficient participation (≤50%)' };
    }

    // Check 3: Yes votes exceed No votes + remaining unvoted stake
    // This means even if all remaining validators vote No, Yes would still win
    const worstCaseNoStake = results.noStake + results.notVotedStake;
    if (results.yesStake <= worstCaseNoStake) {
        return { canPassEarly: false, reason: 'Yes votes insufficient for guaranteed win' };
    }

    return { canPassEarly: true };
}

export function getVoteStatus(vote: GovernanceVoting): VoteStatus {
    const now = Date.now();
    const start = vote.start_timestamp_ms;
    const end = vote.end_timestamp_ms;

    // If vote result is already set to "Passed" or "NotPassed", it's ended
    if (vote.result === 'Passed' || vote.result === 'NotPassed') return 'ended';

    if (now < start) return 'not_started';
    if (now < end) return 'active';
    return 'ended';
}

export function formatTimeRemaining(timestampMs: number): string {
    const now = Date.now();
    const target = timestampMs;
    const diff = target - now;

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

export function formatTimeElapsed(startTimestampMs: number): string {
    const now = Date.now();
    const start = startTimestampMs;
    const diff = now - start;

    if (diff <= 0) return 'Not started';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
}

export function getMockScenarioInfo(): { scenario: string; description: string; status: string } {
    const objectId = import.meta.env.VITE_GOVERNANCE_VOTING_OBJECT_ID;

    const scenarios = {
        'mock': {
            description: 'Default mock scenario (same as mock_active)',
            status: 'active'
        },
        'mock_notstarted': {
            description: 'Vote has not started yet - future start time',
            status: 'not_started'
        },
        'mock_beforecutoff': {
            description: 'Active voting - validators can vote (Yes/No/Abstain)',
            status: 'active'
        },
        'mock_aftercutoff': {
            description: 'Active voting - validators can still vote (Yes/No/Abstain)',
            status: 'active'
        },
        'mock_ended': {
            description: 'Vote has ended - final results available',
            status: 'ended'
        },
        'mock_passing': {
            description: 'Vote is passing (Yes > No AND turnout > 50%)',
            status: 'active'
        },
        'mock_notpassingbutyes': {
            description: 'Yes leads but insufficient turnout (<50% Yes+No participation)',
            status: 'active'
        },
        'mock_notpassingbutno': {
            description: 'No majority despite sufficient turnout',
            status: 'active'
        },
        'mock_early_passing_voting': {
            description: 'Vote passed early (minimum duration elapsed, guaranteed Yes win)',
            status: 'ended'
        },
        'mock_early_passing_staking': {
            description: 'Vote passed early (minimum duration elapsed, guaranteed Yes outcome)',
            status: 'ended'
        }
    };

    const scenario = objectId || 'mock_beforecutoff';
    const info = scenarios[scenario as keyof typeof scenarios] || scenarios['mock_beforecutoff'];

    return {
        scenario,
        description: info.description,
        status: info.status
    };
} 